import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, retry, catchError } from 'rxjs/operators';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Department } from 'src/app/shared/department';
import { Client, CustomerCategory } from 'src/app/shared/client';
import { Person } from 'src/app/shared/person';
import { CliListEntry } from 'src/app/shared/cli-list-entry';
import { PerListEntry } from 'src/app/shared/per-list-entry';
import { AgentCustomerTier } from 'src/app/shared/agent-admin';

/**
 * REST client for clients/companies, contact persons, departments, and
 * customer classification data. Covers the following backend route groups:
 * `/Client`, `/Clients`, `/Person`, `/PersonsList`.
 *
 * Every request includes the standard Webyto headers via
 * {@link GetglobalsettingsService.getStandardApiHeaders}, resolved fresh per
 * call so Setup-screen overrides take effect without a page reload.
 *
 * All methods apply `retry(1)` on network failure. Errors are surfaced via
 * {@link handleError}, which shows a browser `alert` (legacy pattern flagged
 * for Phase 1 replacement).
 */
@Injectable({
  providedIn: 'root'
})
export class ClientApiService {

  apiURL: string = '';

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /**
   * Builds an absolute API URL by prepending the runtime API server address.
   *
   * @param url - Path starting with `/`.
   * @returns Absolute URL to the backend endpoint.
   */
  private _getApiUrl(url: string = ''): string {
    let _api: string = this.globalsettingsservice.getApiServerUrl();
    return _api + url;
  }

  // ---------------------------------------------------------------------------
  // Client / company search and CRUD
  // ---------------------------------------------------------------------------

  /**
   * Searches clients by strict match on the `CLIENTSNIKNAME` (alias/short name)
   * field.
   *
   * Calls `GET /Client/SearchByNick/?_search=<str>`. `str` is URL-encoded here: it is free text typed or pasted by
   * the user, and a raw `&`, `#`, `+` or `%` would cut or change it before it reached the API. An empty or blank
   * string is not an error; the API answers `200 []`.
   *
   * @param str - Search string to match against the nickname field.
   * @returns Observable emitting matching `Client[]` records.
   */
  getClientSearchByNick(str: string): Observable<Client[]> {
    return this.http.get<Client[]>(this._getApiUrl('/Client/SearchByNick/?_search=' + encodeURIComponent(str ?? '')), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Searches clients across all name and address fields using a smart
   * full-text approach.
   *
   * Calls `GET /Client/SimpleSearch/?_search=<str>`. `str` is URL-encoded here (free text typed or pasted by the user:
   * a raw `&`, `#`, `+` or `%` would cut or change it before it reached the API). An empty or blank string is not an
   * error; the API answers `200 []`.
   *
   * @param str - Search string applied across multiple client fields.
   * @returns Observable emitting matching records.
   */
  getClientSimpleSearch(str: string): Observable<any[]> {
    return this.http.get<any[]>(this._getApiUrl('/Client/SimpleSearch/?_search=' + encodeURIComponent(str ?? '')), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the Main Company record for a given client ID.
   *
   * **Corrected 2026-09-20** — this comment previously said it follows the
   * `CLISUBSOF` self-referencing FK; that was never accurate. The backend
   * (`Client.ts`'s `GET /:id/MainCompany`) calls `dbo.ufn_getcompanybylco(@anyId)`,
   * which groups `Clients` rows by `CASTCATID` and returns the row whose
   * `CLIENTSNIKNAME` matches that group's `CustomerCategory.CASTCATNAME` —
   * falling back to `@anyId` itself when it has no `CASTCATID` (not part of
   * any group). `CLISUBSOF` (departments/warehouses within one company) is
   * an unrelated hierarchy this function never touches. See
   * `webyto-client1018/docs/PROMPT_customer-level-discount-auto-set.md`'s
   * "Main Company / Legal Entity hierarchy" section for the full picture.
   *
   * Calls `GET /Client/:id/MainCompany`.
   *
   * @param id - Client ID (`CLIID`).
   * @returns Observable emitting the resolved Main Company `Client[]` record
   *   (always one row — `@anyId` itself when it has no group).
   */
  getMainIdByClientId(id: number): Observable<Client[]> {
    return this.http.get<Client[]>(this._getApiUrl('/Client/' + id + '/MainCompany'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single client record by its primary key.
   *
   * Calls `GET /Client/:id`.
   *
   * @param id - Client ID (`CLIID`).
   * @returns Observable emitting a one-element `Client[]` array.
   */
  getClientById(id: number): Observable<Client[]> {
    return this.http.get<Client[]>(this._getApiUrl('/Client/' + id), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Creates a new client record.
   *
   * Calls `POST /Client/:CLIID`.
   *
   * @param cli - The `Client` record to create.
   * @returns Observable emitting the created `Client`.
   */
  postClientById(cli: Client): Observable<Client> {
    return this.http.post<Client>(this._getApiUrl('/Client/' + cli.CLIID), cli, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates an existing client record.
   *
   * Calls `PUT /Client/:CLIID`.
   *
   * @param cli - The `Client` record to update. `CLIID` must be set.
   * @returns Observable emitting the number of rows affected (Prompt 13-F,
   *   interim response-contract rule — see `docs/PROPOSAL_rest-contract-alignment.md`
   *   in the API repo: writes return a row count, not the entity; re-`GET`
   *   for a fresh read).
   */
  putClientById(cli: Client): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/Client/' + cli.CLIID), cli, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes a client record by its primary key.
   *
   * Calls `DELETE /Client/:CLIID`.
   *
   * @param cli - The `Client` to delete. `CLIID` must be set.
   * @returns Observable emitting the number of rows affected (Prompt 13-F
   *   interim response-contract rule, see `putClientById`).
   */
  deleteClientById(cli: Client): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/Client/' + cli.CLIID), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the departments (sub-companies) belonging to a client.
   *
   * Calls `GET /Client/:id/Departments` (singular `/Client` router,
   * `webyto_api-express4-TS-20250331/routes/Client.ts`).
   *
   * @remarks
   * The capital `D` in `Departments` is intentional here and must not be
   * lowercased to "match" {@link getDepartments} below — that method hits
   * the *plural* `/Clients` router (`routes/Clients.ts`), which declares its
   * own `/departments` route lowercase. The two routers independently chose
   * different casing for a same-named route; this one already matches its
   * own target exactly. (Functionally neither casing matters — Express's
   * `case sensitive routing` setting is never enabled in that repo, so path
   * segments match case-insensitively either way — but keep the two call
   * sites each matching their own router's documented convention rather
   * than uniformly normalizing them.)
   *
   * Confirmed 2026-08-23 this is not a duplicate of {@link getDepartments}
   * below despite the near-identical name and filter logic (both are
   * `CLISUBSOF = :id AND CLISUBSOF <> CLIID` on `[dbo].[Clients]`): this one
   * returns the full `Clients` row (`SELECT *`, hence `Client[]` here) via
   * `Client.ts`; `getDepartments` returns a lean 8-column `Department`
   * projection via `Clients.ts`. Two intentional shapes for two different
   * callers — do not collapse them into one method.
   *
   * @param id - Parent client ID (`CLIID`).
   * @returns Observable emitting an array of department `Client[]` records.
   */
  getDepartmentsByClientId(id: number): Observable<Client[]> {
    return this.http.get<Client[]>(this._getApiUrl('/Client/' + id + '/Departments'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Person / contact person CRUD
  // ---------------------------------------------------------------------------

  /**
   * Creates a new contact person record.
   *
   * Calls `POST /Person/:PERSID`.
   *
   * @param pr - The `Person` to create.
   * @returns Observable emitting the created `Person`.
   */
  postPersonById(pr: Person): Observable<Person> {
    return this.http.post<Person>(this._getApiUrl('/Person/' + pr.PERSID), pr, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates an existing contact person record.
   *
   * Calls `PUT /Person/:PERSID`.
   *
   * @param pr - The `Person` to update. `PERSID` must be set.
   * @returns Observable emitting the number of rows affected (Prompt 13-F
   *   interim response-contract rule, see `putClientById`).
   */
  putPersonById(pr: Person): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/Person/' + pr.PERSID), pr, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes a contact person record.
   *
   * Calls `DELETE /Person/:PERSID`.
   *
   * @param pr - The `Person` to delete. `PERSID` must be set.
   * @returns Observable emitting the number of rows affected (Prompt 13-F
   *   interim response-contract rule, see `putClientById`).
   */
  deletePersonById(pr: Person): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/Person/' + pr.PERSID), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single contact person record by primary key.
   *
   * Calls `GET /Person/:id`.
   *
   * @param id - Person ID (`PERSID`).
   * @returns Observable emitting a one-element `Person[]` array.
   */
  getPersonById(id: number): Observable<Person[]> {
    return this.http.get<Person[]>(this._getApiUrl('/Person/' + id), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns all contact persons belonging to a given client.
   *
   * Calls `GET /Person/ByClient/:id`.
   *
   * @param id - Client ID (`CLIID`).
   * @returns Observable emitting `Person[]` records for that client.
   */
  getPersonsByClientId(id: number): Observable<Person[]> {
    return this.http.get<Person[]>(this._getApiUrl('/Person/ByClient/' + id), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns image metadata for a person or company profile.
   *
   * Calls `GET /Person/:id/Images`.
   *
   * @param id - Person ID (`PERSID`).
   * @returns Observable emitting an array of image metadata records.
   */
  getPersonImagesInfo(id: number): Observable<any[]> {
    return this.http.get<any[]>(this._getApiUrl('/Person/' + id + '/Images'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Client list queries (CliListEntry)
  // ---------------------------------------------------------------------------

  /**
   * Returns the full client list without pagination.
   *
   * Calls `GET /Clients`. Avoid on large datasets — use
   * {@link getCliListSlice} instead.
   *
   * @returns Observable emitting all `CliListEntry[]` records.
   */
  getCliList(): Observable<CliListEntry[]> {
    return this.http.get<CliListEntry[]>(this._getApiUrl('/Clients'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a paginated slice of the client list.
   *
   * Calls `GET /Clients?_start=<startRow>&_end=<endRow>`.
   *
   * @param startRow - First row index (zero-based).
   * @param endRow - Last row index (exclusive).
   * @returns Observable emitting the requested `CliListEntry[]` slice.
   */
  getCliListSlice(startRow: number, endRow: number): Observable<CliListEntry[]> {
    return this.http.get<CliListEntry[]>(this._getApiUrl('/Clients?_start=' + startRow + '&_end=' + endRow), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a paginated, filtered, and sorted slice of the client list.
   *
   * Calls `GET /Clients?_start=...&_end=...&_sf=...&_so=...&_flt=...`.
   *
   * @param startRow - First row index (zero-based).
   * @param endRow - Last row index (exclusive).
   * @param options - Sort and filter options object with `sp` (sort params) and
   *   `fs` (filter string) properties.
   * @returns Observable emitting the matching `CliListEntry[]` slice.
   */
  getCliListSliceFilter(startRow: number, endRow: number, options: any): Observable<CliListEntry[]> {
    let queryString = '?'
      + ('&_start=' + startRow)
      + ('&_end=' + endRow)
      + ((options.sp && options.sp.sf) ? ('&_sf=' + encodeURIComponent(options.sp.sf)) : '')
      + ((options.sp && options.sp.so) ? ('&_so=' + encodeURIComponent(options.sp.so)) : '')
      + (options.fs ? ('&_flt=' + encodeURIComponent(JSON.stringify(options.fs))) : '')
      ;
    return this.http.get<CliListEntry[]>(this._getApiUrl('/Clients' + queryString), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns child clients (sub-companies / PCO entities) of a parent client.
   *
   * Calls `GET /Clients/:parentCliId/children`.
   *
   * @param parentCliId - Parent client ID (`CLIID`).
   * @returns Observable emitting child `CliListEntry[]` records.
   */
  getCliChildList(parentCliId: number): Observable<CliListEntry[]> {
    return this.http.get<CliListEntry[]>(this._getApiUrl('/Clients/' + parentCliId + '/children'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single `CliListEntry` record by client ID.
   *
   * Calls `GET /Clients/:id`.
   *
   * @param id - Client ID (`CLIID`).
   * @returns Observable emitting a one-element `CliListEntry[]` array.
   */
  getCliListEntry(id): Observable<CliListEntry[]> {
    return this.http.get<CliListEntry[]>(this._getApiUrl('/Clients/' + id), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the customer-level discount fact (`AgentCustomerProfiles.tier`)
   * for document pricing. Deliberately a different route than
   * `AgentDataApiService.getCustomer()` (`GET /api/agent/customer/:id`,
   * gated `realm:can-use-agent`) -- this one is `realm:portal-user` since
   * `DocItemPricingService.priceRow()` prices every document, not only
   * agent-sourced ones.
   *
   * Calls `GET /Clients/:id/discount-tier`.
   *
   * @param id - Client ID (`CLIID`).
   * @returns Observable emitting `{ tier: AgentCustomerTier | null }` --
   *   `tier: null` means no profile row exists yet (the common case), never
   *   a 404/error. Caller must treat `null` as "no discount", never as
   *   "inherit the previous customer's discount".
   */
  getDiscountTier(id: number): Observable<{ tier: AgentCustomerTier | null }> {
    return this.http.get<{ tier: AgentCustomerTier | null }>(this._getApiUrl('/Clients/' + id + '/discount-tier'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Department queries
  // ---------------------------------------------------------------------------

  /**
   * Returns all departments (sub-entities with `CLICATEGORY` marking) for a
   * given company.
   *
   * Calls `GET /Clients/:id/departments`.
   *
   * @param id - Parent company client ID (`CLIID`).
   * @returns Observable emitting `Department[]` records.
   */
  getDepartments(id): Observable<Department[]> {
    return this.http.get<Department[]>(this._getApiUrl('/Clients/' + id + '/departments'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns departments of a child company that are linked to a specific
   * parent department.
   *
   * Calls `GET /Clients/:childCliId/childDep/:mainDepId`.
   *
   * @param mainDepId - Parent department ID.
   * @param childCliId - Child company client ID (`CLIID`).
   * @returns Observable emitting matching `Department[]` records.
   */
  getChildDepartmentsById(mainDepId: number, childCliId: number): Observable<Department[]> {
    return this.http.get<Department[]>(this._getApiUrl('/Clients/' + childCliId + '/childDep/' + mainDepId), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns all companies in the same `CASTCATID` group as the given client,
   * including the client itself.
   *
   * The main organisation in the group is identified by:
   * `entry.CLIENTSNIKNAME === entry.CUSTOMERCATEGORY`.
   *
   * Calls `GET /Clients/:id/children`.
   *
   * @param id - Client ID (`CLIID`).
   * @returns Observable emitting `CliListEntry[]`.
   */
  getClientChildren(id: number): Observable<CliListEntry[]> {
    return this.http.get<CliListEntry[]>(
      this._getApiUrl('/Clients/' + id + '/children'),
      { headers: this.globalsettingsservice.getStandardApiHeaders() }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  // ---------------------------------------------------------------------------
  // Person list queries (PerListEntry)
  // ---------------------------------------------------------------------------

  /**
   * Returns a single `PerListEntry` record by person ID.
   *
   * Calls `GET /PersonsList/PERSID/:id`.
   *
   * @param id - Person ID (`PERSID`).
   * @returns Observable emitting a one-element `PerListEntry[]` array.
   */
  getPerListEntry(id:number): Observable<PerListEntry[]> {
    return this.http.get<PerListEntry[]>(this._getApiUrl('/PersonsList/PERSID/'+id), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the full persons list without pagination.
   *
   * Calls `GET /PersonsList`. Avoid on large datasets — use
   * {@link getPerListSlice} instead.
   *
   * @returns Observable emitting all `PerListEntry[]` records.
   */
  getPerList(): Observable<PerListEntry[]> {
    return this.http.get<PerListEntry[]>(this._getApiUrl('/PersonsList'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a paginated slice of the persons list.
   *
   * Calls `GET /PersonsList?_start=<startRow>&_end=<endRow>`.
   *
   * @param startRow - First row index (zero-based).
   * @param endRow - Last row index (exclusive).
   * @returns Observable emitting the requested `PerListEntry[]` slice.
   */
  getPerListSlice(startRow: number, endRow: number): Observable<PerListEntry[]> {
    return this.http.get<PerListEntry[]>(this._getApiUrl('/PersonsList?_start=' + startRow + '&_end=' + endRow), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a paginated, filtered, and sorted slice of the persons list.
   *
   * Calls `GET /PersonsList?_start=...&_end=...&_sf=...&_so=...&_flt=...`.
   *
   * @param startRow - First row index (zero-based).
   * @param endRow - Last row index (exclusive).
   * @param options - Sort and filter options with `sp` and `fs` properties.
   * @returns Observable emitting the matching `PerListEntry[]` slice.
   */
  getPerListSliceFilter(startRow: number, endRow: number, options: any): Observable<PerListEntry[]> {
    let queryString = '?'
      + ('&_start=' + startRow)
      + ('&_end=' + endRow)
      + ((options.sp && options.sp.sf) ? ('&_sf=' + encodeURIComponent(options.sp.sf)) : '')
      + ((options.sp && options.sp.so) ? ('&_so=' + encodeURIComponent(options.sp.so)) : '')
      + (options.fs ? ('&_flt=' + encodeURIComponent(JSON.stringify(options.fs))) : '')
      ;
    return this.http.get<PerListEntry[]>(this._getApiUrl('/PersonsList' + queryString), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns all persons belonging to a specific company.
   *
   * Calls `GET /PersonsList/:id`.
   *
   * @param id - Company client ID (`CLIID`).
   * @returns Observable emitting `PerListEntry[]` records for that company.
   */
  getPerListByCompany(id: number): Observable<PerListEntry[]> {
    return this.http.get<PerListEntry[]>(this._getApiUrl('/PersonsList/' + id), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Customer classification
  // ---------------------------------------------------------------------------

  /**
   * Returns all distinct customer business area strings used across clients.
   *
   * Calls `GET /Clients/CustomerBusinessAreas/`.
   *
   * @returns Observable emitting an array of business area label strings.
   */
  getCustomerBusinessAreaList(): Observable<string[]> {
    return this.http.get<string[]>(this._getApiUrl('/Clients/CustomerBusinessAreas/'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the business area string for a specific client.
   *
   * Calls `GET /Clients/:id/CustomerBusinessArea`.
   *
   * @param id - Client ID (`CLIID`).
   * @returns Observable emitting a one-element string array.
   */
  getCustomerBusinessAreaByID(id: number): Observable<string[]> {
    return this.http.get<string[]>(this._getApiUrl('/Clients/'+id+'/CustomerBusinessArea'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates the business area classification for a client.
   *
   * Calls `PUT /Clients/:CLIID/CustomerBusinessArea`.
   *
   * @param cli - The `Client` whose `CUSTOMERTYPE` field should be updated.
   * @returns Observable emitting the updated `[{ CLIID, CUSTOMERTYPE }]`.
   */
  putCustomerBusinessAreaByID(cli: Client): Observable<[{ CLIID: number, CUSTOMERTYPE: string}]> {
    return this.http.put<[{ CLIID: number, CUSTOMERTYPE: string }]>(this._getApiUrl('/Clients/' + cli.CLIID + '/CustomerBusinessArea'), cli, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns all customer category records used to classify client types.
   *
   * Calls `GET /Clients/CustomerCategories/`.
   *
   * @returns Observable emitting an array of `CustomerCategory` records.
   */
  getCustomerCategoryList(): Observable<CustomerCategory[]> {
    return this.http.get<CustomerCategory[]>(this._getApiUrl('/Clients/CustomerCategories/'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single customer category by its ID.
   *
   * Calls `GET /Clients/CustomerCategory/:id`.
   *
   * @param id - Category ID (`CASTCATID`).
   * @returns Observable emitting a one-element `CustomerCategory[]` array.
   */
  getCustomerCategoryByID(id: number): Observable<CustomerCategory[]> {
    return this.http.get<CustomerCategory[]>(this._getApiUrl('/Clients/CustomerCategory/' + id), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates an existing customer category record.
   *
   * Calls `PUT /Clients/CustomerCategory/:CASTCATID`.
   *
   * @param castCategory - The `CustomerCategory` to update. `CASTCATID` must
   *   be set.
   * @returns Observable emitting the number of rows affected (Prompt 13-F
   *   interim response-contract rule, see `putClientById`). No call sites
   *   in this app as of this writing — confirmed via a repo-wide grep.
   */
  putCustomerCategoryByID(castCategory: CustomerCategory): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/Clients/CustomerCategory/' + castCategory.CASTCATID), castCategory, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Creates a new customer category record.
   *
   * Calls `POST /Clients/CustomerCategory/:CASTCATID`.
   *
   * @param castCategory - The `CustomerCategory` to create.
   * @returns Observable emitting the created `CustomerCategory`.
   */
  postCustomerCategoryByID(castCategory: CustomerCategory): Observable<CustomerCategory> {
    return this.http.post<CustomerCategory>(this._getApiUrl('/Clients/CustomerCategory/' + castCategory.CASTCATID), castCategory, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the financial balance due between the main company and a specific
   * client, across up to four stock/warehouse dimensions.
   *
   * Calls `GET /Clients/:cliid/BalanceWithCustomer/:cm/:cr/:s1/:s2/:s3/:s4/`.
   *
   * @param cliid - Client ID (`CLIID`).
   * @param cm - Main company ID.
   * @param cr - Currency ID for balance conversion.
   * @param s1 - First warehouse/stock dimension ID.
   * @param s2 - Second warehouse/stock dimension ID.
   * @param s3 - Third warehouse/stock dimension ID.
   * @param s4 - Fourth warehouse/stock dimension ID.
   * @param td - Reference date for the balance calculation.
   * @returns Observable emitting balance summary records.
   */
  getClientBalanceDue(cliid: number, cm: number, cr: number, s1: number, s2: number, s3: number, s4: number, td: Date): Observable<any[]> {
    return this.http.get<any[]>(this._getApiUrl('/Clients/' + cliid + '/BalanceWithCustomer/' + cm + '/' + cr + '/' + s1 + '/' + s2 + '/' + s3 + '/' + s4 + '/?_toDate=' + td.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Image management
  // ---------------------------------------------------------------------------

  /**
   * Fetches a single image record by its primary key.
   * IMAGEDATA is always returned as a base64 string.
   * IMAGENOTE contains the MIME type string (e.g. 'image/png').
   *
   * Calls `GET /Images/:id`.
   *
   * @param imageId - Image record ID (`IMAGEID`).
   * @returns Observable emitting the image record.
   */
  getImageById(imageId: number): Observable<{ IMAGEID: number; IMAGETYPE: number; IMAGESIZE: number; IMAGEDATA: string; IMAGENOTE: string }> {
    return this.http.get<any>(this._getApiUrl('/Images/' + imageId), { headers: this.globalsettingsservice.getStandardApiHeaders() }).pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Uploads or replaces the company logo. Creates an Images record (IMAGETYPE=1)
   * and updates `Clients.CLILOGOID`. Replaces atomically if one already exists.
   *
   * Calls `POST /Client/:cliId/Logo`.
   *
   * @param cliId - Client company ID.
   * @param mimeType - MIME type string (e.g. `'image/png'`), stored in IMAGENOTE.
   * @param imageData - Raw base64 string (without `data:...;base64,` prefix).
   * @returns Observable emitting `{ CLILOGOID: number }`.
   *
   * @remarks
   * `imageType` in the body (here `1`, matching `IMAGETYPE`) is intentional
   * forward-looking metadata — the backend route currently derives which
   * image slot this is from the URL (`/Logo` vs `/Stamp` vs `/Sign`) and
   * does not yet read this field from the body at all (harmless — Express
   * ignores unrecognized body fields). Keep sending it: it's meant for a
   * future backend change that reads `imageType` directly instead of
   * inferring it from the route, without a frontend change needed when that
   * lands. Not dead code, not a bug — an intentionally early client-side
   * field. See `uploadClientStamp`/`uploadPersonSign` below for the same
   * pattern (`imageType: 2`/`3`).
   */
  uploadClientLogo(cliId: number, mimeType: string, imageData: string): Observable<{ CLILOGOID: number }> {
    return this.http.post<{ CLILOGOID: number }>(
      this._getApiUrl('/Client/' + cliId + '/Logo'),
      { imageType: 1, imageData, imageNote: mimeType },
      { headers: this.globalsettingsservice.getStandardApiHeaders() }
    ).pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Removes the company logo. Sets `Clients.CLILOGOID = NULL` and deletes the Images row.
   *
   * Calls `DELETE /Client/:cliId/Logo`.
   */
  deleteClientLogo(cliId: number): Observable<{ CLILOGOID: null }> {
    return this.http.delete<{ CLILOGOID: null }>(
      this._getApiUrl('/Client/' + cliId + '/Logo'),
      { headers: this.globalsettingsservice.getStandardApiHeaders() }
    ).pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Uploads or replaces the company stamp image (IMAGETYPE=2).
   *
   * Calls `POST /Client/:cliId/Stamp`.
   *
   * @remarks See {@link uploadClientLogo}'s remarks — `imageType` here is
   * the same intentional forward-looking field, not yet read backend-side.
   */
  uploadClientStamp(cliId: number, mimeType: string, imageData: string): Observable<{ CLISTAMPID: number }> {
    return this.http.post<{ CLISTAMPID: number }>(
      this._getApiUrl('/Client/' + cliId + '/Stamp'),
      { imageType: 2, imageData, imageNote: mimeType },
      { headers: this.globalsettingsservice.getStandardApiHeaders() }
    ).pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Removes the company stamp image. Sets `Clients.CLISTAMPID = NULL`.
   *
   * Calls `DELETE /Client/:cliId/Stamp`.
   */
  deleteClientStamp(cliId: number): Observable<{ CLISTAMPID: null }> {
    return this.http.delete<{ CLISTAMPID: null }>(
      this._getApiUrl('/Client/' + cliId + '/Stamp'),
      { headers: this.globalsettingsservice.getStandardApiHeaders() }
    ).pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Uploads or replaces a person's handwritten signature image (IMAGETYPE=3).
   *
   * Calls `POST /Person/:persId/Sign`.
   *
   * @remarks See {@link uploadClientLogo}'s remarks — `imageType` here is
   * the same intentional forward-looking field, not yet read backend-side.
   */
  uploadPersonSign(persId: number, mimeType: string, imageData: string): Observable<{ PERSSIGNID: number }> {
    return this.http.post<{ PERSSIGNID: number }>(
      this._getApiUrl('/Person/' + persId + '/Sign'),
      { imageType: 3, imageData, imageNote: mimeType },
      { headers: this.globalsettingsservice.getStandardApiHeaders() }
    ).pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Removes a person's signature image. Sets `Person.PERSSIGNID = NULL`.
   *
   * Calls `DELETE /Person/:persId/Sign`.
   */
  deletePersonSign(persId: number): Observable<{ PERSSIGNID: null }> {
    return this.http.delete<{ PERSSIGNID: null }>(
      this._getApiUrl('/Person/' + persId + '/Sign'),
      { headers: this.globalsettingsservice.getStandardApiHeaders() }
    ).pipe(retry(1), catchError(this.handleError));
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  /**
   * Centralised HTTP error handler used by all methods in this service.
   *
   * Extracts a human-readable message, shows a browser `alert` (legacy pattern
   * — Phase 1 will replace with a toast), and re-throws so the calling
   * Observable terminates.
   *
   * @param error - Angular `HttpErrorResponse` or `ErrorEvent`.
   * @returns An Observable that immediately errors with the formatted message.
   */
  handleError(error) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(errorMessage);
  }
}
